import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  adminLogin,
  adminMe,
  getToken,
  clearToken,
  fetchBlockedDates,
  setBlockedDates,
} from "@/lib/api";
import { ro } from "date-fns/locale";
import { format } from "date-fns";
import { toast } from "sonner";
import { LogOut, Lock, CalendarOff, RefreshCw } from "lucide-react";

const toIso = (d) => format(d, "yyyy-MM-dd");

function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await adminLogin(password);
      toast.success("Bun venit!");
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Eroare la autentificare";
      toast.error(typeof msg === "string" ? msg : "Parolă incorectă");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-5">
      <Card className="w-full max-w-sm bg-[#fbf6ea] border-[#a17a4c]/30 p-7 rounded-2xl">
        <div className="w-12 h-12 rounded-full bg-[#8a6a3f] flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-[#f7f1e6]" />
        </div>
        <h1 className="font-serif-display text-3xl text-[#4a3520]">
          Admin · Skyline
        </h1>
        <p className="text-sm text-[#5a4530] mt-1 mb-6">
          Introdu parola pentru a gestiona disponibilitatea.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid="admin-password-input"
            type="password"
            placeholder="Parolă"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-white border-[#a17a4c]/40 h-11"
            autoFocus
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#8a6a3f] hover:bg-[#7a5d38] text-[#f7f1e6] h-11 rounded-full"
          >
            {loading ? "Se verifică..." : "Intră în panou"}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function CabinAvailability({ cabinKey, title }) {
  const [blocked, setBlocked] = useState([]); // array of ISO strings
  const [range, setRange] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const dates = await fetchBlockedDates(cabinKey);
      setBlocked(dates);
    } catch {
      toast.error("Nu am putut încărca datele blocate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinKey]);

  const blockedDateObjs = blocked.map((s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  });

  // Build the list of ISO dates between range.from..range.to (inclusive)
  const rangeAsIso = () => {
    if (!range?.from) return [];
    const end = range.to || range.from;
    const list = [];
    const cur = new Date(range.from);
    while (cur <= end) {
      list.push(toIso(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return list;
  };

  const apply = async (block) => {
    const dates = rangeAsIso();
    if (!dates.length) {
      toast.error("Selectează o perioadă în calendar");
      return;
    }
    setBusy(true);
    try {
      const next = await setBlockedDates(cabinKey, dates, block);
      setBlocked(next);
      setRange(undefined);
      toast.success(
        block
          ? `${dates.length} ${dates.length === 1 ? "zi blocată" : "zile blocate"}`
          : `${dates.length} ${dates.length === 1 ? "zi eliberată" : "zile eliberate"}`
      );
    } catch (e) {
      toast.error("Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      data-testid={`admin-${cabinKey}-card`}
      className="bg-[#fbf6ea] border-[#a17a4c]/30 p-5 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif-display text-2xl text-[#4a3520]">{title}</h2>
        <button
          onClick={reload}
          className="text-xs text-[#8a6a3f] hover:text-[#4a3520] inline-flex items-center gap-1"
          data-testid={`admin-${cabinKey}-refresh`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Reîmprospătează
        </button>
      </div>

      <div className="text-xs text-[#5a4530] mb-3">
        Click pe o zi (sau selectează interval) → apoi <strong>Blochează</strong> sau
        <strong> Eliberează</strong>. Zilele roșii sunt deja ocupate.
      </div>

      <div className="rounded-xl bg-[#f3e8d0]/60 p-2 flex justify-center">
        <Calendar
          data-testid={`admin-${cabinKey}-calendar`}
          mode="range"
          selected={range}
          onSelect={setRange}
          numberOfMonths={1}
          locale={ro}
          modifiers={{ booked: blockedDateObjs }}
          modifiersStyles={{
            booked: {
              backgroundColor: "rgba(180, 40, 40, 0.18)",
              color: "#9b2c2c",
              textDecoration: "line-through",
              borderRadius: "8px",
            },
          }}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          data-testid={`admin-${cabinKey}-block-btn`}
          onClick={() => apply(true)}
          disabled={busy || !range?.from}
          className="bg-[#9b2c2c] hover:bg-[#7a2222] text-[#f7f1e6] rounded-full"
        >
          <CalendarOff className="w-4 h-4 mr-2" />
          Blochează perioada
        </Button>
        <Button
          data-testid={`admin-${cabinKey}-unblock-btn`}
          onClick={() => apply(false)}
          disabled={busy || !range?.from}
          variant="outline"
          className="border-[#8a6a3f] text-[#4a3520] hover:bg-[#efe5d3] rounded-full"
        >
          Eliberează perioada
        </Button>
        {range?.from && (
          <Button
            onClick={() => setRange(undefined)}
            variant="ghost"
            className="text-[#5a4530] hover:bg-[#efe5d3] rounded-full"
            data-testid={`admin-${cabinKey}-reset`}
          >
            Resetează
          </Button>
        )}
      </div>

      <div className="mt-4 text-[11px] text-[#8a6a44]">
        {blocked.length} zile blocate în total
      </div>
    </Card>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const navigate = useNavigate();

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-[#5a4530] text-sm">Se încarcă...</div>
      </div>
    );
  }

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-page">
      <header className="border-b border-[#a17a4c]/20 bg-[#f7f1e6]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#8a6a3f]" />
            <span className="font-serif-display text-xl text-[#4a3520]">
              Skyline · Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              size="sm"
              className="border-[#a17a4c]/40 text-[#4a3520] hover:bg-[#efe5d3] rounded-full"
              data-testid="admin-go-site"
            >
              Vezi site‑ul
            </Button>
            <Button
              onClick={() => {
                clearToken();
                setAuthed(false);
              }}
              size="sm"
              className="bg-[#8a6a3f] hover:bg-[#7a5d38] text-[#f7f1e6] rounded-full"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-serif-display text-4xl text-[#4a3520]">
            Gestionare disponibilitate
          </h1>
          <p className="text-[#5a4530] mt-2 max-w-xl text-sm">
            Marchează zilele când cabanele sunt ocupate. Vizitatorii nu le vor
            putea selecta pe calendarul public.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <CabinAvailability cabinKey="S1" title="Cabana S1" />
          <CabinAvailability cabinKey="S2" title="Cabana S2" />
        </div>
      </main>
    </div>
  );
}
